import { useTranslation } from 'react-i18next';
import { SearchX, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * PUSTY WYNIK Z WYJŚCIEM.
 *
 * Do 27.09 pusty wynik był jednym zdaniem w pustej kolumnie („Brak ofert dopasowanych do
 * Twoich filtrów."), bez ani jednego przycisku — a na telefonie w widoku mapy było jeszcze
 * gorzej: przy zerze wyników mapa w ogóle się nie montowała, więc razem z nią znikały
 * „Rysuj", „Dzielnice" i +/−. Człowiek zostawał na ekranie, z którego nie dało się wyjść
 * inaczej niż przez cofnięcie w przeglądarce (zgłoszenie Maćka, MOB-01).
 *
 * Wzór bierzemy z własnego produktu: stan pusty ulubionych ma ikonę, nagłówek, zdanie i
 * przycisk — i tak ma wyglądać każdy nasz pusty ekran.
 *
 * DWA UŁOŻENIA, JEDEN KOMPONENT. `plywajacy` kładzie kartę NA mapie (telefon, widok mapy):
 * mapa zostaje pod spodem, żywa i sterowalna. Bez tego to zwykły blok w kolumnie listy —
 * ten sam tekst i te same akcje, więc pusty wynik nie mówi dwóch różnych rzeczy w dwóch
 * miejscach.
 */
export function PustyWynik({
  naWyczysc,
  naUsunObszar,
  poluzuj,
  plywajacy = false,
  className,
}: {
  /** Zdejmuje wszystkie filtry. Zawsze jest — to główne wyjście. */
  naWyczysc: () => void;
  /** Zdejmuje sam obszar; podawane tylko wtedy, gdy jakiś jest wybrany. */
  naUsunObszar?: () => void;
  /**
   * CO POLUZOWAĆ (pusty wynik po wejściu z „Mojego wyszukiwania", ustalenia 28.09): filtry
   * uporządkowane od tego, który najbardziej zawęża, każdy z przyciskiem zdjęcia. Bez liczb —
   * „ile ukryto" to kuchnia, nie informacja dla człowieka (zasada Piotra 28.09).
   */
  poluzuj?: { label: string; naUsun: () => void }[];
  plywajacy?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="pusty-wynik"
      data-plywajacy={plywajacy ? 'pusty-wynik' : undefined}
      className={cn(
        'mx-auto flex max-w-sm flex-col items-center gap-3 rounded-2xl bg-white text-center',
        plywajacy ? 'p-4' : 'p-5',
        plywajacy
          ? // Nad mapą, ale nie NA jej kontrolkach: górna krawędź należy do „Rysuj" i
            // „Dzielnice", a prawa, w połowie wysokości, do kolumny z legendą i +/−.
            // Dlatego karta stoi między lewą krawędzią a tą kolumną (3,5 rem zapasu), a nie
            // pośrodku — inaczej zasłaniałaby przyciski, których właśnie ma nie zabierać.
            // Pionowo ustawia ją warstwa w SearchPage (pas między atrybucją a pigułką „Lista”).
            'pointer-events-auto w-full shadow-lg ring-1 ring-black/5'
          : 'py-12',
        className,
      )}
    >
      {/* Ikona TYLKO w układzie blokowym. Na mapie karta musi zmieścić się między
          atrybucją a pigułką „Lista" — zmierzone okno to 261 px, a karta z ikoną miała
          296 px i zachodziła na jedno i drugie. Ikona jest tu ozdobą; tekst i przyciski
          nie są. */}
      {!plywajacy && (
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <SearchX className="h-5 w-5" />
        </span>
      )}
      <p className="text-base font-semibold text-foreground">{t('search.empty_title')}</p>
      <p className="text-sm text-muted-foreground">{t('search.empty_hint')}</p>
      {poluzuj && poluzuj.length > 0 && (
        <div data-testid="co-poluzowac" className="w-full space-y-2 pt-1">
          <p className="text-xs font-medium text-muted-foreground">{t('search.empty_relax')}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {poluzuj.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={p.naUsun}
                aria-label={t('search.filters.clear_one', { name: p.label })}
                className="flex items-center gap-1.5 rounded-full border bg-muted/40 px-3 py-1.5 text-xs font-medium hover:bg-muted"
              >
                {p.label}
                <X className="h-3 w-3 opacity-60" />
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
        <Button type="button" onClick={naWyczysc} className="rounded-full">
          {t('search.clear_all')}
        </Button>
        {naUsunObszar && (
          <Button type="button" variant="outline" onClick={naUsunObszar} className="rounded-full">
            {t('search.empty_clear_area')}
          </Button>
        )}
      </div>
    </div>
  );
}

export default PustyWynik;
